import { Button, ButtonGroup, FormGroup, H1, H3, Switch, Text,
  //  TextArea 
  } from "@blueprintjs/core";
import { Businesses, Routes } from "@just-in-types";
import QRCode from "qrcode.react";
import * as React from "react";
import styled from "react-emotion";
import { connect } from "react-redux";
import { Editor } from '@tinymce/tinymce-react';
import { emptyRoutes } from "../adminFunctions/cleanSites";
import { IState } from "../store";
import { getSiteData, getSiteSnapshot, updateSiteRoute } from "../store/actions";
import { emptySite } from "../views/Businesses";
import CopyLink from "./CopyLink";
import {
  ISiteTreeDispatchProps,
  ISiteTreeOwnProps,
  ISiteTreeStateProps,
  RoutesObjectDisabled,
  SiteTreeProps,
  SiteTreeState,
} from "./SiteTree.d";
import SiteTreeOptions from "./SiteTreeOptions";

const WaiverTextWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 20rem;
`;

const RouteSwitchGroup: React.SFC<{
  alwaysShow: boolean;
  alwaysShowDisabled: boolean;
  enabled: boolean;
  enabledDisabled: boolean;
  onChangeHandler: (event: React.FormEvent<HTMLInputElement>) => void;
  optional: boolean;
  optionalDisabled: boolean;
  routeName: Routes.RouteString;
}> = ({
  alwaysShow,
  alwaysShowDisabled,
  children,
  enabled,
  enabledDisabled,
  onChangeHandler,
  optional,
  optionalDisabled,
  routeName,
}) => (
  <FormGroup label={routeName}>
    <Switch
      checked={alwaysShow}
      disabled={alwaysShowDisabled}
      data-property-of={routeName}
      label="always show"
      onChange={onChangeHandler}
      name="alwaysShow"
    />
    <Switch
      checked={enabled}
      disabled={enabledDisabled}
      data-property-of={routeName}
      label="enabled"
      onChange={onChangeHandler}
      name="enabled"
    />
    <Switch
      checked={optional}
      disabled={optionalDisabled}
      data-property-of={routeName}
      label="optional"
      onChange={onChangeHandler}
      name="optional"
    />
    {children}
  </FormGroup>
);

class SiteTree extends React.PureComponent<SiteTreeProps, SiteTreeState> {
  private initialState: SiteTreeState = {
    ...emptySite,
    site: null,
  };

  constructor(props: SiteTreeProps) {
    super(props);

    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getSiteData();
  }

  public componentDidUpdate({ siteId: prevSiteId }: SiteTreeProps) {
    const { siteId, unsubscribe } = this.props;

    if (prevSiteId !== siteId) {
      if (unsubscribe) {
        unsubscribe();
      }

      this.setState(this.initialState);

      this.getSiteData();
    }
  }

  public componentWillUnmount() {
    const { unsubscribe } = this.props;

    if (unsubscribe) {
      unsubscribe();
    }
  }

  public render() {
    const {
      disabled: {
        covidquestions: covidquestionsDisabled,
        customertype: customertypeDisabled,
        dateofbirth: dateofbirthDisabled,
        email: emailDisabled,
        language: languageDisabled,
        name: nameDisabled,
        signature: signatureDisabled,
        supercard: supercardDisabled,
        vehicles: vehiclesDisabled,
        zip: zipDisabled,
      },
      routes: {
        covidquestions,
        customertype,
        dateofbirth,
        email,
        language,
        name,
        signature,
        signature: { takeMugshot, waiverText, waiverTextEs },
        supercard,
        vehicles,
        zip,
      },
      site,
    } = this.state;

    if (site) {
      const { businessId, businessPath, siteId, url, qrUrl } = this.props;
      const { path } = site;
      const { waiverText: waiverTextProp, waiverTextEs: waiverTextEsProp } = site.routes.signature;
      const waiverTextHasChanged = waiverTextProp !== waiverText;
      const waiverTextEsHasChanged = waiverTextEsProp !== waiverTextEs;

      let checkQr: string | null | undefined = null;
      if (site.hasOwnProperty("s3Id")) {
        checkQr = site.s3Id ? site.s3Id : null;
      } else if (site.hasOwnProperty("urgId")) {
        checkQr = site.urgId ? site.urgId : null;
      }

      return (
        <div
          style={{
            alignItems: "flex-start",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <H1>{path}</H1>
          <CopyLink url={url} />
          <SiteTreeOptions businessId={businessId} site={site} siteId={siteId} />
          {checkQr && (
            <div style={{ marginBottom: "2rem" }}>
              <H3>Just In QR Code</H3>
              <QRCode id="qr-code" value={qrUrl} size={200} level={"H"} />
              <p>
                <a
                  style={{
                    display: "inline-block",
                    fontWeight: "bold",
                    marginTop: "5px",
                  }}
                  onClick={this.downloadQR}
                >
                  Download QR{" "}
                </a>
              </p>
              <H3>Just in landing page link</H3>
              <CopyLink url={"https://just-in.texnrewards.com/just-in/" + businessPath + "/" + path} />
            </div>
          )}
          <hr />
          <H3>Kiosk screens</H3>
          {dateofbirth && (
            <RouteSwitchGroup
              alwaysShow={dateofbirth.alwaysShow}
              alwaysShowDisabled={dateofbirthDisabled.alwaysShow}
              enabled={dateofbirth.enabled}
              enabledDisabled={dateofbirthDisabled.enabled}
              onChangeHandler={this.handleSwitchChange}
              optional={dateofbirth.optional}
              optionalDisabled={dateofbirthDisabled.optional}
              routeName="dateofbirth"
            />
          )}
          <RouteSwitchGroup
            alwaysShow={email.alwaysShow}
            alwaysShowDisabled={emailDisabled.alwaysShow}
            enabled={email.enabled}
            enabledDisabled={emailDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={email.optional}
            optionalDisabled={emailDisabled.optional}
            routeName="email"
          />
          <RouteSwitchGroup
            alwaysShow={language.alwaysShow}
            alwaysShowDisabled={languageDisabled.alwaysShow}
            enabled={language.enabled}
            enabledDisabled={languageDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={language.optional}
            optionalDisabled={languageDisabled.optional}
            routeName="language"
          />
          <RouteSwitchGroup
            alwaysShow={name.alwaysShow}
            alwaysShowDisabled={nameDisabled.alwaysShow}
            enabled={name.enabled}
            enabledDisabled={nameDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={name.optional}
            optionalDisabled={nameDisabled.optional}
            routeName="name"
          />
          <RouteSwitchGroup
            alwaysShow={signature.alwaysShow}
            alwaysShowDisabled={signatureDisabled.alwaysShow}
            enabled={signature.enabled}
            enabledDisabled={signatureDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={signature.optional}
            optionalDisabled={signatureDisabled.optional}
            routeName="signature"
          >
            <Switch
              checked={takeMugshot}
              disabled={signatureDisabled.takeMugshot}
              data-property-of="signature"
              label="take picture"
              name="takeMugshot"
              onChange={this.handleSwitchChange}
            />
            <WaiverTextWrapper>
              <Text>waiver text</Text>
              <Editor apiKey="v1hwp9ut2otdw9gnvuhoif4a9vw7myjycv7dl5sc6w9r12ck"
    data-property-of="signature"
    disabled={signatureDisabled.waiverText}

    onChange={(evt,edot)=> this.updateTextWaiver(evt)}
    initialValue={waiverText}
    textareaName='waiverText'
         init={{
           height: 700,
           menubar: true,
           plugins: [
             'advlist autolink lists link image charmap print preview anchor',
             'searchreplace visualblocks code fullscreen',
             'insertdatetime media table paste code help wordcount', 
             'fullscreen'        ,
             'emoticons'  ,
             'image',
             'spellchecker',
             'table' 
           ],
           toolbar: 'insertfile undo redo | formatselect | ' +
           'bold italic backcolor | alignleft aligncenter ' +
           'alignright alignjustify | spellchecker|  bullist numlist outdent indent | ' +
           'removeformat | link | emoticons | image | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | fullscreen | help'    
           ,
           content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
         }}      
          />

              <ButtonGroup>
                <Button
                  disabled={signatureDisabled.waiverText || !waiverTextHasChanged}
                  intent="danger"
                  onClick={this.revertWaiverText}
                >
                  revert
                </Button>
                <Button
                  data-property-of="signature"
                  disabled={signatureDisabled.waiverText || !waiverTextHasChanged}
                  intent="primary"
                  name="waiverText"
                  onClick={this.handleTextUpdate}
                >
                  change waiver
                </Button>
              </ButtonGroup>
            </WaiverTextWrapper>
            <WaiverTextWrapper>
              <Text>waiver text (spanish) (optional)</Text>
              <Editor apiKey="v1hwp9ut2otdw9gnvuhoif4a9vw7myjycv7dl5sc6w9r12ck"
    data-property-of="signature"
    disabled={signatureDisabled.waiverTextEs}
    onChange={(evt,edot)=> this.updateTextWaiverEs(evt)}
    initialValue={waiverTextEs}
    textareaName='waiverTextEs'
    init={{
      height: 700,
      menubar: true,
      plugins: [
        'advlist autolink lists link image charmap print preview anchor',
        'searchreplace visualblocks code fullscreen',
        'insertdatetime media table paste code help wordcount', 
        'fullscreen'        ,
        'emoticons'  ,
        'image',
        'spellchecker',
        'table'
        
      ],
      toolbar: 'insertfile undo redo | formatselect | ' +
      'bold italic backcolor | alignleft aligncenter ' +
      'alignright alignjustify | spellchecker|  bullist numlist outdent indent | ' +
      'removeformat | link | emoticons | image | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | fullscreen | help'    
      ,
      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
    }}
       />
              <ButtonGroup>
                <Button
                  disabled={signatureDisabled.waiverTextEs || !waiverTextEsHasChanged}
                  intent="danger"
                  onClick={this.revertWaiverTextEs}
                >
                  revert
                </Button>
                <Button
                  data-property-of="signature"
                  disabled={signatureDisabled.waiverTextEs || !waiverTextEsHasChanged}
                  intent="primary"
                  name="waiverTextEs"
                  onClick={this.handleTextUpdate}
                >
                  change waiver
                </Button>
              </ButtonGroup>
            </WaiverTextWrapper>
          </RouteSwitchGroup>
          <RouteSwitchGroup
            alwaysShow={vehicles.alwaysShow}
            alwaysShowDisabled={vehiclesDisabled.alwaysShow}
            enabled={vehicles.enabled}
            enabledDisabled={vehiclesDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={vehicles.optional}
            optionalDisabled={vehiclesDisabled.optional}
            routeName="vehicles"
          />
          <RouteSwitchGroup
            alwaysShow={zip.alwaysShow}
            alwaysShowDisabled={zipDisabled.alwaysShow}
            enabled={zip.enabled}
            enabledDisabled={zipDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={zip.optional}
            optionalDisabled={zipDisabled.optional}
            routeName="zip"
          />
          <RouteSwitchGroup
            alwaysShow={customertype.alwaysShow}
            alwaysShowDisabled={customertypeDisabled.alwaysShow}
            enabled={customertype.enabled}
            enabledDisabled={customertypeDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={customertype.optional}
            optionalDisabled={customertypeDisabled.optional}
            routeName="customertype"
          />
          <RouteSwitchGroup
            alwaysShow={supercard.alwaysShow}
            alwaysShowDisabled={supercardDisabled.alwaysShow}
            enabled={supercard.enabled}
            enabledDisabled={supercardDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={supercard.optional}
            optionalDisabled={supercardDisabled.optional}
            routeName="supercard"
          />
          <RouteSwitchGroup
            alwaysShow={covidquestions.alwaysShow}
            alwaysShowDisabled={covidquestionsDisabled.alwaysShow}
            enabled={covidquestions.enabled}
            enabledDisabled={covidquestionsDisabled.enabled}
            onChangeHandler={this.handleSwitchChange}
            optional={covidquestions.optional}
            optionalDisabled={covidquestionsDisabled.optional}
            routeName="covidquestions"
          />
        </div>
      );
    } else {
      return null;
    }
  }

  private cleanRoute = (
    dirtyRoute: any,
    cleanRoute: Routes.Route | Routes.SignatureRoute
  ): Routes.Route | Routes.SignatureRoute => {
    if ("alwaysShow" in dirtyRoute && typeof dirtyRoute.alwaysShow === "boolean") {
      cleanRoute.alwaysShow = dirtyRoute.alwaysShow;
    }
    if ("enabled" in dirtyRoute && typeof dirtyRoute.enabled === "boolean") {
      cleanRoute.enabled = dirtyRoute.enabled;
    }
    if ("optional" in dirtyRoute && typeof dirtyRoute.optional === "boolean") {
      cleanRoute.optional = dirtyRoute.optional;
    }

    return cleanRoute;
  };

  private cleanSignatureRoute = (dirtyRoute: any, emptyRoute: Routes.SignatureRoute) => {
    const cleanRoute = this.cleanRoute(dirtyRoute, emptyRoute);

    if ("takeMugshot" in cleanRoute) {
      if ("takeMugshot" in dirtyRoute && typeof dirtyRoute.takeMugshot === "boolean") {
        cleanRoute.takeMugshot = dirtyRoute.takeMugshot;
      }
      if ("waiverText" in dirtyRoute && typeof dirtyRoute.waiverText === "string") {
        cleanRoute.waiverText = dirtyRoute.waiverText;
      }
      if ("waiverTextEs" in dirtyRoute && typeof dirtyRoute.waiverTextEs === "string") {
        cleanRoute.waiverTextEs = dirtyRoute.waiverTextEs;
      }
    }

    return cleanRoute;
  };

  private cleanRoutes = (dirtyRoutes: any): Routes.RoutesObject => {
    const cleanRoutes = emptyRoutes;

    if ("dateofbirth" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.dateofbirth, cleanRoutes.dateofbirth);
    }
    if ("email" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.email, cleanRoutes.email);
    }
    if ("language" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.language, cleanRoutes.language);
    }
    if ("name" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.name, cleanRoutes.name);
    }
    if ("signature" in dirtyRoutes) {
      this.cleanSignatureRoute(dirtyRoutes.signature, cleanRoutes.signature);
    }
    if ("vehicles" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.vehicles, cleanRoutes.vehicles);
    }
    if ("zip" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.zip, cleanRoutes.zip);
    }
    if ("customertype" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.customertype, cleanRoutes.customertype);
    }
    if ("supercard" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.supercard, cleanRoutes.supercard);
    }
    if ("covidquestions" in dirtyRoutes) {
      this.cleanRoute(dirtyRoutes.covidquestions, cleanRoutes.covidquestions);
    }

    return cleanRoutes;
  };

  private buildSiteRoutes = (site: Businesses.Site) => {
    const { routes: dirtyRoutes } = site;
    const routes = this.cleanRoutes(dirtyRoutes);

    this.setState({ routes, site });
  };

  private getSiteData = async () => {
    const { businessId, siteId } = this.props;

    const site = await getSiteData({ businessId, siteId });

    if (site) {
      this.buildSiteRoutes(site);
    }
  };

  private handleSwitchChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { site } = this.state;
    const {
      checked,
      dataset: { propertyOf },
      name,
    } = event.currentTarget;

    if (
      site &&
      propertyOf &&
      (name === "alwaysShow" || name === "enabled" || name === "takeMugshot" || name === "optional")
    ) {
      const disabled = { ...this.state.disabled };
      const routes = { ...this.state.routes };
      const route = propertyOf as Routes.RouteString;

      disabled[route][name] = true;
      routes[route][name] = checked;

      this.setState({ routes });

      this.sendRouteUpdate(disabled, routes, route, name);
    }
  };



  private handleTextUpdate = (event: React.MouseEvent<HTMLButtonElement>) => {
    const {
      dataset: { propertyOf },
      name,
    } = event.currentTarget;
    const disabled = { ...this.state.disabled };
    const routes = { ...this.state.routes };

    if (propertyOf && name === "waiverText") {
      const route = propertyOf as Routes.RouteString;
      this.sendRouteUpdate(disabled, routes, route, name);
      alert('Successfully Updated WaiverText');
  
    }

    if (propertyOf && name === "waiverTextEs") {
      const route = propertyOf as Routes.RouteString;
      this.sendRouteUpdate(disabled, routes, route, name);
      alert('Successfully Updated WaiverTextEs');
    }


  };

  
  private updateTextWaiver(target){

    const routes = { ...this.state.routes };
     routes['signature']['waiverText'] = target.level.content;
    this.setState({ routes });
  
   
};

private updateTextWaiverEs(target){

  const routes = { ...this.state.routes };
   routes['signature']['waiverTextEs'] = target.level.content;
  this.setState({ routes });

};




  private sendRouteUpdate = async (
    disabled: RoutesObjectDisabled,
    routes: Businesses.Site["routes"],
    route: string,
    name: string
  ) => {
    const { businessId, onUpdateSiteRoute, siteId } = this.props;
    const { site } = this.state;

    if (site) {
      const { routes: routesProp } = site;
      const updateSuccessful = await onUpdateSiteRoute({
        businessId,
        routes,
        siteId,
      });

      disabled[route][name] = false;

      if (updateSuccessful) {
        this.setState({ disabled });
      } else {
        routes[route][name] = routesProp[route][name];

        this.setState({ disabled, routes });
      }
    }
  };

  private revertWaiverText = () => {
    const { site } = this.state;

    if (site) {
      const waiverText = site.routes.signature.waiverText;
      const routes = { ...this.state.routes };

      routes.signature.waiverText = waiverText;

      this.setState({ routes });
    }
  };

  private revertWaiverTextEs = () => {
    const { site } = this.state;

    if (site) {
      const waiverTextEs = site.routes.signature.waiverTextEs;
      const routes = { ...this.state.routes };

      routes.signature.waiverTextEs = waiverTextEs;

      this.setState({ routes });
    }
  };

  private downloadQR = () => {
    const canvas = document.getElementById("qr-code") as HTMLCanvasElement;
    if (canvas) {
      const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
      const downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = "qr.png";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };
}

const mapStateToProps = ({ businesses: { site, unsubscribeSite } }: IState) => ({ site, unsubscribe: unsubscribeSite });

const mapDispatchToProps = {
  onGetSiteSnapshot: getSiteSnapshot,
  onUpdateSiteRoute: updateSiteRoute,
};

export default connect<ISiteTreeStateProps, ISiteTreeDispatchProps, ISiteTreeOwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(SiteTree);



// WEBPACK FOOTER //
// ./src/components/SiteTree.tsx